/**
 * Notification preferences utility functions
 * Handles mapping and setting notification preferences for users
 */

// Notification types for file owner notifications
const FILE_OWNER_NOTIFICATION_TYPES = [
  'email',
  'documents',
  'fileOwner',
  'sms',
  'fax',
  'statusNote',
];

// Notification types for watched notifications
const WATCHED_NOTIFICATION_TYPES = [
  'email',
  'documents',
  'fileOwner',
  'sms',
  'fax',
  'statusNote',
];

// Notification types for property notifications
const PROPERTY_NOTIFICATION_TYPES = ['email', 'sms', 'fax'];

/**
 * Maps notification preferences from userdata to user object
 * @param {Object} user - The user object to update
 * @param {Object} userdata - The raw user data from API
 * @param {string} prefix - The prefix for notification properties (empty for file owner, 'watched' for watched)
 * @param {boolean} hasPermission - Whether user has permission for this notification type
 * @param {Array} notificationTypes - Array of notification types to process
 */
const mapNotificationPreferences = (
  user,
  userdata,
  prefix,
  hasPermission,
  notificationTypes
) => {
  if (hasPermission) {
    // Set real-time notification preferences
    notificationTypes.forEach((type) => {
      // Convert camelCase to snake_case for backend field names
      const snakeCaseType = type.replace(/([A-Z])/g, '_$1').toLowerCase();
      const propertyName = prefix
        ? `watched_receive_new_${snakeCaseType}_notifications`
        : `receive_new_${snakeCaseType}_notifications`;

      const value = userdata[propertyName] ?? false;
      user[propertyName] = value;
    });

    // Set email notification preferences
    notificationTypes.forEach((type) => {
      // Convert camelCase to snake_case for backend field names
      const snakeCaseType = type.replace(/([A-Z])/g, '_$1').toLowerCase();
      const propertyName = prefix
        ? `watched_receive_new_${snakeCaseType}_notifications_email`
        : `receive_new_${snakeCaseType}_notifications_email`;

      user[propertyName] = userdata[propertyName] ?? false;
    });

    // Set watched-specific properties
    if (prefix === 'watched') {
      user.watched_certifications = userdata.watched_certifications ?? [];
    }
  } else {
    // Set all preferences to false when user doesn't have permission
    notificationTypes.forEach((type) => {
      // Convert camelCase to snake_case for backend field names
      const snakeCaseType = type.replace(/([A-Z])/g, '_$1').toLowerCase();
      const propertyName = prefix
        ? `watched_receive_new_${snakeCaseType}_notifications`
        : `receive_new_${snakeCaseType}_notifications`;

      user[propertyName] = false;
    });

    notificationTypes.forEach((type) => {
      // Convert camelCase to snake_case for backend field names
      const snakeCaseType = type.replace(/([A-Z])/g, '_$1').toLowerCase();
      const propertyName = prefix
        ? `watched_receive_new_${snakeCaseType}_notifications_email`
        : `receive_new_${snakeCaseType}_notifications_email`;

      user[propertyName] = false;
    });

    // Set watched-specific properties to defaults
    if (prefix === 'watched') {
      user.watched_certifications = [];
    }
  }
};

/**
 * Sets up file owner notification preferences
 * @param {Object} user - The user object to update
 * @param {Object} userdata - The raw user data from API
 */
export const setupFileOwnerNotificationPreferences = (user, userdata) => {
  mapNotificationPreferences(
    user,
    userdata,
    '', // no prefix for file owner notifications
    user.canUseRealTimeNotificationsFileOwner,
    FILE_OWNER_NOTIFICATION_TYPES
  );
};

/**
 * Sets up watched notification preferences
 * @param {Object} user - The user object to update
 * @param {Object} userdata - The raw user data from API
 */
export const setupWatchedNotificationPreferences = (user, userdata) => {
  mapNotificationPreferences(
    user,
    userdata,
    'watched', // prefix for watched notifications
    user.canUseRealTimeNotificationsWatched,
    WATCHED_NOTIFICATION_TYPES
  );
};

/**
 * Sets up property notification preferences
 * @param {Object} user - The user object to update
 * @param {Object} userdata - The raw user data from API
 */
export const setupPropertyNotificationPreferences = (user, userdata) => {
  // Property notifications use a different mapping pattern
  if (user.canUseRealTimeNotificationsProperty) {
    // Set real-time notification preferences
    PROPERTY_NOTIFICATION_TYPES.forEach((type) => {
      const propertyName = `receive_property_${type}_notifications`;
      const value = userdata[propertyName] ?? false;
      user[propertyName] = value;
    });

    // Set email notification preferences
    PROPERTY_NOTIFICATION_TYPES.forEach((type) => {
      const propertyName = `receive_property_${type}_notifications_email`;
      const value = userdata[propertyName] ?? false;
      user[propertyName] = value;
    });
  } else {
    // Set all preferences to false when user doesn't have permission
    PROPERTY_NOTIFICATION_TYPES.forEach((type) => {
      const propertyName = `receive_property_${type}_notifications`;
      user[propertyName] = false;
    });

    PROPERTY_NOTIFICATION_TYPES.forEach((type) => {
      const propertyName = `receive_property_${type}_notifications_email`;
      user[propertyName] = false;
    });
  }
};

/**
 * Sets up all notification preferences for a user
 * @param {Object} user - The user object to update
 * @param {Object} userdata - The raw user data from API
 */
export const setupAllNotificationPreferences = (user, userdata) => {
  setupFileOwnerNotificationPreferences(user, userdata);
  setupWatchedNotificationPreferences(user, userdata);
  setupPropertyNotificationPreferences(user, userdata);
};

/**
 * Formats communication preference for display
 * @param {string} preference - The communication preference value
 * @returns {string} Formatted preference string
 */
export const formatCommunicationPreference = (preference) => {
  if (!preference) {
    return 'Email (default)';
  }
  switch (preference) {
    case 'email':
      return 'Email';
    case 'mobile_phone':
    case 'text':
      return 'SMS';
    default:
      return preference;
  }
};
